import { get_db, PROJECT_SESSIONS_STORE } from './db'

/**
 * Persisted in-progress project sessions: model bytes + the step the user
 * reached + the chosen skeleton, so a refresh or accidental close can be
 * recovered from "Resume project" rather than re-uploading the model.
 *
 * Scope (v1): only what's strictly needed to put the user back at the same
 * step with their model loaded. Edit-skeleton overrides, animation state, and
 * weight tweaks are NOT persisted here — those still come from the existing
 * `ProjectArchive` JSON export or get re-derived on resume.
 *
 * Failure handling: any IDB error (private browsing, quota, missing API) is
 * caught and logged; storage becomes a no-op rather than crashing the app,
 * mirroring the pattern in `UserClipStorage` / `CustomSkeletonStorage`.
 */

export interface ProjectSession {
  id: string
  name: string
  created_at: string
  updated_at: string
  /** ProcessStep value the user had reached when this snapshot was taken. */
  last_step: string
  model: {
    /** Raw bytes of the user's uploaded model file. */
    bytes: ArrayBuffer
    /** File extension without the leading dot (e.g., "glb", "fbx", "zip"). */
    extension: string
    /** Original filename — used as the default session name and shown in the UI. */
    file_name: string
  }
  /** Either a built-in `SkeletonType` value or `custom:<id>` referring to a
   *  CustomSkeletonStorage entry. Empty string when the user hasn't chosen yet. */
  skeleton_selection: string
  /** Optional preview image captured from the renderer canvas. */
  thumbnail_data_url?: string
}

export class ProjectSessionStorage {
  static async list (): Promise<ProjectSession[]> {
    try {
      const db = await get_db()
      const all = await db.getAll(PROJECT_SESSIONS_STORE) as unknown[]
      const valid: ProjectSession[] = []
      for (const raw of all) {
        if (is_project_session(raw)) valid.push(raw)
      }
      // Most recent first.
      valid.sort((a, b) => b.updated_at.localeCompare(a.updated_at))
      return valid
    } catch (err) {
      console.warn('ProjectSessionStorage.list failed:', err)
      return []
    }
  }

  static async get (id: string): Promise<ProjectSession | null> {
    try {
      const db = await get_db()
      const raw = await db.get(PROJECT_SESSIONS_STORE, id) as unknown
      if (raw === undefined || !is_project_session(raw)) return null
      return raw
    } catch (err) {
      console.warn(`ProjectSessionStorage.get(${id}) failed:`, err)
      return null
    }
  }

  static async put (session: ProjectSession): Promise<boolean> {
    try {
      const db = await get_db()
      const updated: ProjectSession = { ...session, updated_at: new Date().toISOString() }
      await db.put(PROJECT_SESSIONS_STORE, updated)
      return true
    } catch (err) {
      console.warn(`ProjectSessionStorage.put(${session.id}) failed:`, err)
      return false
    }
  }

  static async delete (id: string): Promise<boolean> {
    try {
      const db = await get_db()
      await db.delete(PROJECT_SESSIONS_STORE, id)
      return true
    } catch (err) {
      console.warn(`ProjectSessionStorage.delete(${id}) failed:`, err)
      return false
    }
  }

  static async clear_all (): Promise<void> {
    try {
      const db = await get_db()
      await db.clear(PROJECT_SESSIONS_STORE)
    } catch (err) {
      console.warn('ProjectSessionStorage.clear_all failed:', err)
    }
  }
}

export function generate_session_id (): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  let out = ''
  for (let i = 0; i < 32; i++) {
    out += Math.floor(Math.random() * 16).toString(16)
  }
  return out
}

function is_project_session (raw: unknown): raw is ProjectSession {
  if (typeof raw !== 'object' || raw === null) return false
  const r = raw as Partial<ProjectSession>
  if (typeof r.id !== 'string') return false
  if (typeof r.name !== 'string') return false
  if (typeof r.created_at !== 'string' || typeof r.updated_at !== 'string') return false
  if (typeof r.last_step !== 'string') return false
  if (typeof r.skeleton_selection !== 'string') return false
  if (typeof r.model !== 'object' || r.model === null) return false
  const m = r.model
  if (!(m.bytes instanceof ArrayBuffer)) return false
  if (typeof m.extension !== 'string' || typeof m.file_name !== 'string') return false
  if (r.thumbnail_data_url !== undefined && typeof r.thumbnail_data_url !== 'string') return false
  return true
}
